// src/components/ConquestGame.js
import React, { useState, useEffect, useRef } from 'react';

const ConquestGame = ({ room_id, map: initialMap, my_id, opponent_id, start_time, duration, initial_positions, initial_scores, socket, userId }) => {
  const [map, setMap] = useState(initialMap);
  const [scores, setScores] = useState(initial_scores);
  const [positions, setPositions] = useState(initial_positions);
  const [timeLeft, setTimeLeft] = useState(duration); // 直接使用 duration 作为初始值
  const [gameEnded, setGameEnded] = useState(false);
  const [winner, setWinner] = useState(null);
  const [gameResult, setGameResult] = useState(null);
  const [showResultOverlay, setShowResultOverlay] = useState(false);
  const intervalRef = useRef();

  // 响应式格子大小
  const [cellSize, setCellSize] = useState(40);
  const updateCellSize = () => {
    const container = document.getElementById('conquest-grid');
    if (!container) return;
    const containerWidth = container.clientWidth;
    const cols = map[0]?.length || 10;
    let size = Math.floor(containerWidth / cols);
    size = Math.min(60, Math.max(30, size));
    setCellSize(size);
  };

  useEffect(() => {
    updateCellSize();
    window.addEventListener('resize', updateCellSize);
    return () => window.removeEventListener('resize', updateCellSize);
  }, [map]);

  // 倒计时（前端独立计时，每秒减1）
  useEffect(() => {
    if (gameEnded) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setGameEnded(true);
          setShowResultOverlay(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [gameEnded]);

  // 监听服务器广播
  useEffect(() => {
    const handleStateUpdate = (data) => {
      setPositions(data.positions);
      setMap(data.map);
      setScores(data.scores);
    };
    const handleGameResult = (data) => {
      setGameEnded(true);
      setWinner(data.winner_id);
      setGameResult(data);
      setShowResultOverlay(true);
    };
    socket.on('conquest_state_update', handleStateUpdate);
    socket.on('conquest_game_result', handleGameResult);
    return () => {
      socket.off('conquest_state_update', handleStateUpdate);
      socket.off('conquest_game_result', handleGameResult);
    };
  }, [socket]);

  const sendMove = (direction) => {
    if (gameEnded) return;
    socket.emit('conquest_move', { room_id, token: localStorage.getItem('memo_token'), direction });
  };

  // 键盘控制
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key;
      let direction = null;
      if (key === 'w' || key === 'ArrowUp') direction = 'up';
      if (key === 's' || key === 'ArrowDown') direction = 'down';
      if (key === 'a' || key === 'ArrowLeft') direction = 'left';
      if (key === 'd' || key === 'ArrowRight') direction = 'right';
      if (direction) {
        e.preventDefault();
        sendMove(direction);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sendMove]);

  const handleExit = () => {
    if (window.confirm('退出游戏将判负，确定吗？')) {
      socket.emit('conquest_forfeit', { room_id, token: localStorage.getItem('memo_token') });
      window.location.href = '/game-center';
    }
  };

  const handleBackToCenter = () => {
    window.location.href = '/game-center';
  };

  const handleCloseOverlay = () => {
    setShowResultOverlay(false);
  };

  const renderCell = (cell, x, y) => {
    let bgColor = '#2c3e50';
    if (cell.type === 'obstacle') bgColor = '#7f8c8d';
    else if (cell.owner === userId) bgColor = '#e74c3c';
    else if (cell.owner) bgColor = '#3498db';
    else bgColor = '#ecf0f1';
    
    const isMe = positions[userId]?.x === x && positions[userId]?.y === y;
    const isOpponent = positions[opponent_id]?.x === x && positions[opponent_id]?.y === y;
    
    return (
      <div
        key={`${x}-${y}`}
        style={{
          width: `${cellSize}px`,
          height: `${cellSize}px`,
          backgroundColor: bgColor,
          outline: '1px solid #bdc3c7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: `${cellSize * 0.5}px`,
          boxSizing: 'border-box',
        }}
      >
        {isMe && '😀'}
        {isOpponent && '😈'}
      </div>
    );
  };

  const rows = map.map((row, y) => ({ row, y }));

  const renderGameOverlay = () => {
    if (!showResultOverlay) return null;
    const isWinner = winner === userId;
    let title = '';
    let message = '';
    if (winner === null) {
        title = '🤝 平局';
        message = '双方分数相同，战成平局！';
    } else if (isWinner) {
        title = '🎉 胜利！';
        message = '你占领了更多格子，胜利！';
    } else {
        title = '💀 失败';
        message = '对手占领了更多格子，失败';
    }
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(5px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
            background: 'rgba(30,30,40,0.95)',
            borderRadius: '20px',
            padding: '30px',
            textAlign: 'center',
            maxWidth: '400px',
            width: '80%',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            border: '1px solid rgba(255,255,255,0.2)'
        }}>
            <h2 style={{ margin: '0 0 20px', color: winner === null ? '#f39c12' : (isWinner ? '#f39c12' : '#e74c3c') }}>
              {title}
            </h2>
            <p style={{ color: '#ddd', marginBottom: '20px' }}>{message}</p>
            <p style={{ color: '#aaa', fontSize: '14px', marginBottom: '20px' }}>
            你的得分: {scores[userId]} &nbsp;|&nbsp; 对手得分: {scores[opponent_id]}
            </p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
            <button
              onClick={handleCloseOverlay}
              style={{
                background: '#2c3e50',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '30px',
                color: 'white',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
                继续查看棋盘
            </button>
            <button
                onClick={handleBackToCenter}
                style={{
                background: '#f39c12',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '30px',
                color: 'white',
                fontSize: '16px',
                cursor: 'pointer'
                }}
            >
                返回游戏中心
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ textAlign: 'center', padding: '10px', position: 'relative' }}>
      {renderGameOverlay()}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
        <button onClick={handleExit} style={{ background: '#e74c3c', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '20px', cursor: 'pointer' }}>🚪 退出游戏</button>
        <div style={{ fontSize: '18px' }}>🎨 你的分数: {scores[userId] || 0} &nbsp;|&nbsp; 👤 对手分数: {scores[opponent_id] || 0}</div>
        <div style={{ fontSize: '18px' }}>⏱️ {timeLeft}秒</div>
      </div>

      <div id="conquest-grid" style={{ overflowX: 'auto', padding: '5px', backgroundColor: '#34495e', borderRadius: '10px' }}>
        {rows.map(({ row, y }) => (
          <div key={y} style={{ display: 'flex', justifyContent: 'center', lineHeight: 0, gap: '2px', margin: '2px 0' }}>
            {row.map((cell, x) => renderCell(cell, x, y))}
          </div>
        ))}
      </div>

      {/* 手机虚拟方向键 */}
      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', maxWidth: '220px', width: '100%' }}>
          <div></div>
          <button onClick={() => sendMove('up')} style={{ padding: '15px', fontSize: '24px', touchAction: 'manipulation' }}>↑</button>
          <div></div>
          <button onClick={() => sendMove('left')} style={{ padding: '15px', fontSize: '24px', touchAction: 'manipulation' }}>←</button>
          <button onClick={() => sendMove('down')} style={{ padding: '15px', fontSize: '24px', touchAction: 'manipulation' }}>↓</button>
          <button onClick={() => sendMove('right')} style={{ padding: '15px', fontSize: '24px', touchAction: 'manipulation' }}>→</button>
        </div>
      </div>
    </div>
  );
};

export default ConquestGame;